import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../ui/card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Copy, Check, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useApi } from "../../lib/ApiContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { validateTemplatePath } from "../../lib/validation";

// LLM models options - OpenAI models only for marketing landing pages
const llmModelOptions = [
    { value: "gpt-4o", label: "GPT-4o - Enhanced creative writing" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini - Fast and cost-effective" },
    { value: "gpt-4.1", label: "GPT-4.1 - Latest flagship model" },
    { value: "gpt-4.1-mini", label: "GPT-4.1 Mini - Fast and efficient" },
    { value: "gpt-5.6", label: "GPT-5.6 - Latest model" },
    { value: "gpt-5.5", label: "GPT-5.5 - Previous generation" },
    { value: "o3", label: "OpenAI o3 - Advanced reasoning" },
    { value: "o4-mini", label: "OpenAI o4 Mini - Fast and efficient" },
    { value: "gpt-4.1-nano", label: "GPT-4.1 Nano - Fast and efficient" },
];

// Temperature options
const temperatureOptions = [
    { value: 0.0, label: "0.0 - Very deterministic" },
    { value: 0.1, label: "0.1" },
    { value: 0.2, label: "0.2" },
    { value: 0.3, label: "0.3" },
    { value: 0.4, label: "0.4" },
    { value: 0.5, label: "0.5 - Balanced" },
    { value: 0.6, label: "0.6" },
    { value: 0.7, label: "0.7" },
    { value: 0.8, label: "0.8" },
    { value: 0.9, label: "0.9" },
    { value: 1.0, label: "1.0" },
    { value: 1.1, label: "1.1" },
    { value: 1.2, label: "1.2 - Very creative" },
];

const generateToken = () => {
    // Generate a random token with a timestamp to ensure uniqueness
    const timestamp = new Date().getTime();
    const random = Math.random().toString(36).substring(2, 10);
    return 'tp_' + timestamp + random;
};

const TemplateNew = () => {
    const { orgPublicId } = useParams();
    const navigate = useNavigate();
    const api = useApi();

    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generalError, setGeneralError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        llm_model: 'gpt-4o-mini',
        temperature: 0.7,
        daily_rate_limit: 2000,
        llm_context: '',
        llm_prompt: '',
        master_html_url: '',
        template_path: '',
        html_selectors: [],
        dynamic_selector_prefix: '',
        brand_voice_origin_sites: '',
        organization_id: '', // Will be set after fetching organization
        noindex: false,
    });
    const [newSelector, setNewSelector] = useState({
        name: '',
        selector_path: ''
    });
    const [selectorMode, setSelectorMode] = useState('manual');
    const [isSaving, setIsSaving] = useState(false);
    const [brandVoiceStore, setBrandVoiceStore] = useState(null);
    const [originSites, setOriginSites] = useState([]);
    const [isBrandVoiceLoading, setIsBrandVoiceLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const org = await api.organizations.getByPublicId(orgPublicId);
                if (org) {
                    setOrganization(org);

                    // Set the organization_id in form data to the database ID
                    setFormData(prev => ({
                        ...prev,
                        organization_id: org.id
                    }));

                    // Fetch brand voice data for this organization
                    try {
                        setIsBrandVoiceLoading(true);
                        const brandVoiceStoreData = await api.organizations.getBrandVoiceStore(org.id);
                        setBrandVoiceStore(brandVoiceStoreData);

                        // If we have a brand voice store, fetch origin sites
                        if (brandVoiceStoreData && brandVoiceStoreData.openai_vector_store_id) {
                            try {
                                const originSitesData = await api.serviceBindings.listOriginSites(brandVoiceStoreData.openai_vector_store_id);
                                if (originSitesData && originSitesData.success && originSitesData.origin_sites) {
                                    setOriginSites(originSitesData.origin_sites);
                                } else {
                                    setOriginSites([]);
                                }
                            } catch (err) {
                                console.error("Failed to fetch origin sites:", err);
                                setOriginSites([]);
                            }
                        } else {
                            setOriginSites([]);
                        }
                    } catch (err) {
                        console.error("Failed to fetch brand voice store:", err);
                        setBrandVoiceStore(null);
                        setOriginSites([]);
                    } finally {
                        setIsBrandVoiceLoading(false);
                    }
                } else {
                    setGeneralError("Organization not found");
                }
            } catch (err) {
                console.error("Failed to fetch organization:", err);
                setGeneralError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orgPublicId, api.organizations]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Special handling for daily_rate_limit to ensure it's an integer
        if (name === 'daily_rate_limit') {
            // Convert to integer and ensure it's not negative
            const intValue = parseInt(value, 10);
            setFormData((prev) => ({
                ...prev,
                [name]: isNaN(intValue) ? '' : Math.max(0, intValue)
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error for this field when it's modified
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRegenerateToken = () => {
        setFormData((prev) => ({
            ...prev,
            token: generateToken()
        }));
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(formData.token);
        setShowCopiedTooltip(true);
        setTimeout(() => {
            setShowCopiedTooltip(false);
        }, 2000);
    };

    const validateForm = () => {
        const errors = {};

        // Validate template_path ends with a slash
        const templatePathValidation = validateTemplatePath(formData.template_path);
        if (!templatePathValidation.valid) {
            errors.template_path = templatePathValidation.message;
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            return;
        }

        setIsCreating(true);
        // Clear any existing general errors
        setGeneralError(null);

        try {
            // Submit the form data to create a new template
            const response = await api.templates.create(formData);
            // Navigate back to organization edit
            navigate(`/organizations/${organization.public_id}/edit`);
        } catch (err) {
            console.error("Failed to create template:", err);

            // Check if this is a duplicate template_path error
            if (err.message && err.message.includes('UNIQUE constraint failed') &&
                err.message.includes('template_path')) {

                // Use field-specific error only for template path issues
                const newFieldErrors = {
                    template_path: 'A template with this path already exists in this organization. Please use a different template path.'
                };
                setFieldErrors(newFieldErrors);

                // Force a scroll after a short delay to ensure rendering has completed
                setTimeout(() => {
                    const element = document.getElementById('template_path');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element.focus();
                    }
                }, 100);
            } else {
                // For other errors, show a general error at the top
                setGeneralError(err.message);
            }

            setIsCreating(false);
        }
    };

    const handleCancel = () => {
        navigate(`/organizations/${organization.public_id}/edit`);
    };

    // Handle selector input change
    const handleSelectorInputChange = (e) => {
        const { name, value } = e.target;
        setNewSelector({
            ...newSelector,
            [name]: value
        });
    };

    // Handle adding a new selector
    const handleAddSelector = () => {
        if (newSelector.name.trim() === '' || newSelector.selector_path.trim() === '') {
            return; // Don't add empty selectors
        }

        const newSelectorWithId = {
            ...newSelector,
            id: Date.now() // Use timestamp as a simple ID generator
        };

        setFormData(prev => ({
            ...prev,
            html_selectors: [...prev.html_selectors, newSelectorWithId]
        }));

        // Reset the new selector input fields
        setNewSelector({
            name: '',
            selector_path: ''
        });
    };

    // Handle removing a selector
    const handleRemoveSelector = (id) => {
        setFormData(prev => ({
            ...prev,
            html_selectors: prev.html_selectors.filter(selector => selector.id !== id)
        }));
    };

    // Handle editing an existing selector
    const handleEditSelector = (index, field, value) => {
        const updatedSelectors = [...formData.html_selectors];
        updatedSelectors[index] = {
            ...updatedSelectors[index],
            [field]: value
        };
        setFormData(prev => ({
            ...prev,
            html_selectors: updatedSelectors
        }));
    };

    // Check if manual selection should be disabled
    const isManualSelectionDisabled = formData.dynamic_selector_prefix && formData.dynamic_selector_prefix.trim() !== '';

    // If dynamic selector prefix changes, switch to dynamic tab if a value is entered
    useEffect(() => {
        if (isManualSelectionDisabled && selectorMode === 'manual') {
            setSelectorMode('dynamic');
        }
    }, [formData.dynamic_selector_prefix, isManualSelectionDisabled, selectorMode]);

    // Handle brand voice origin sites selection change
    const handleBrandVoiceOriginSitesChange = (selectedValues) => {
        const originSitesString = selectedValues.join(',');
        setFormData(prev => ({
            ...prev,
            brand_voice_origin_sites: originSitesString
        }));
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div
                        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"
                        data-testid="loading-spinner"
                        role="status"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        Organization not found
                    </AlertDescription>
                </Alert>
                <div className="mt-4">
                    <Button variant="outline" onClick={() => navigate('/organizations')}>
                        Back to Organizations
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold">New Template</h1>
                    <p className="text-gray-500">
                        Organization: {organization.name}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate(`/organizations/${organization.public_id}/edit`)}
                >
                    Back to Organization
                </Button>
            </div>

            <form onSubmit={handleSubmit}>
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Template Details</CardTitle>
                        <CardDescription>
                            Basic information about the template
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="name" className="text-sm font-medium">
                                Template Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="e.g., Blog Post Template"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="unique_key" className="text-sm font-medium">
                                Template ID
                            </label>
                            <input
                                id="unique_key"
                                name="unique_key"
                                type="text"
                                value="Will be generated"
                                className="w-full p-2 border rounded-md font-mono text-sm bg-gray-50"
                                readOnly
                                disabled
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                A unique 5-digit identifier will be generated when the template is created.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>LLM Configuration</CardTitle>
                        <CardDescription>
                            Configure how the LLM should process this template
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="space-y-2">
                                <label htmlFor="llm_model" className="text-sm font-medium">
                                    LLM Model
                                </label>
                                <Select
                                    name="llm_model"
                                    value={formData.llm_model}
                                    onValueChange={(value) => handleSelectChange('llm_model', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select LLM Model" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {llmModelOptions.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="temperature" className="text-sm font-medium">
                                    Temperature
                                </label>
                                <Select
                                    name="temperature"
                                    value={formData.temperature.toString()}
                                    onValueChange={(value) => handleSelectChange('temperature', parseFloat(value))}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select Temperature" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {temperatureOptions.map(option => (
                                            <SelectItem key={option.value} value={option.value.toString()}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="daily_rate_limit" className="text-sm font-medium">
                                    Daily Rate Limit
                                </label>
                                <div className="relative w-full">
                                    <input
                                        id="daily_rate_limit"
                                        name="daily_rate_limit"
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={formData.daily_rate_limit}
                                        onChange={handleInputChange}
                                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="2000"
                                    />
                                </div>
                                <p className="text-xs text-gray-500">
                                    Maximum number of pages that can be created per day
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="llm_context" className="text-sm font-medium">
                                LLM Context
                            </label>
                            <Textarea
                                id="llm_context"
                                name="llm_context"
                                value={formData.llm_context}
                                onChange={handleInputChange}
                                className="min-h-[150px]"
                                placeholder="Enter the context/system prompt for the LLM"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Additional context to help the AI understand the template purpose.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="llm_prompt" className="text-sm font-medium">
                                LLM Prompt
                            </label>
                            <Textarea
                                id="llm_prompt"
                                name="llm_prompt"
                                value={formData.llm_prompt}
                                onChange={handleInputChange}
                                className="min-h-[100px]"
                                placeholder="Enter the prompt template with variables"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Instructions for how the AI should generate content. Use {'{keyword}'} to reference the keyword.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">
                                Brand Voice Origin Sites
                            </label>
                            {isBrandVoiceLoading ? (
                                <div className="flex items-center py-4">
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
                                    <span className="text-sm text-gray-600">Loading brand voice data...</span>
                                </div>
                            ) : originSites.length > 0 ? (
                                <div className="space-y-3">
                                    <p className="text-xs text-gray-500">
                                        Select which brand voice documents to include when generating content for this template.
                                    </p>
                                    <div className="grid grid-cols-1 gap-3 max-h-40 overflow-y-auto border rounded-md p-3">
                                        {originSites.map((site) => {
                                            const isSelected = formData.brand_voice_origin_sites
                                                .split(',')
                                                .filter(s => s.trim())
                                                .includes(site.origin_site);

                                            return (
                                                <div key={site.origin_site} className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id={`origin-site-${site.origin_site}`}
                                                        checked={isSelected}
                                                        onCheckedChange={(checked) => {
                                                            const currentSites = formData.brand_voice_origin_sites
                                                                .split(',')
                                                                .filter(s => s.trim());

                                                            let newSites;
                                                            if (checked) {
                                                                newSites = [...currentSites, site.origin_site];
                                                            } else {
                                                                newSites = currentSites.filter(s => s !== site.origin_site);
                                                            }

                                                            handleBrandVoiceOriginSitesChange(newSites);
                                                        }}
                                                    />
                                                    <Label
                                                        htmlFor={`origin-site-${site.origin_site}`}
                                                        className="text-sm font-normal cursor-pointer flex-1"
                                                    >
                                                        <div className="flex justify-between">
                                                            <span>{site.origin_site}</span>
                                                            <span className="text-gray-500">
                                                                {site.document_count} document{site.document_count !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                    </Label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4 text-gray-500 border rounded-md">
                                    <p className="text-sm">No brand voice documents found for this organization.</p>
                                    <p className="text-xs mt-1">Add documents in the Organization settings to enable brand voice integration.</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Page URLs</CardTitle>
                        <CardDescription>
                            Configure the URLs for this template
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="master_html_url" className="text-sm font-medium">
                                Master HTML Template URL
                            </label>
                            <input
                                id="master_html_url"
                                name="master_html_url"
                                type="url"
                                value={formData.master_html_url}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="https://example.com/template-path"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                This is the URL of the page that will be used as a template.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="template_path" className="text-sm font-medium">
                                Template Path
                            </label>
                            <div className="flex items-center">
                                <div className="bg-gray-100 px-3 py-2 border border-r-0 rounded-l-md text-gray-500 font-mono text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                    {organization?.custom_domain || 'https://domain.com'}
                                </div>
                                <input
                                    id="template_path"
                                    name="template_path"
                                    type="text"
                                    value={formData.template_path}
                                    onChange={handleInputChange}
                                    className={`flex-1 p-2 border rounded-r-md ${fieldErrors.template_path ? 'border-red-500 ring-1 ring-red-500 focus:ring-2 focus:ring-red-500' : ''}`}
                                    placeholder="landing-page/"
                                />
                            </div>
                            {fieldErrors.template_path && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600">
                                    <p className="text-sm font-medium">
                                        {fieldErrors.template_path}
                                    </p>
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                The path where generated pages will be publicly accessible. Must end with a slash (/).
                            </p>
                        </div>

                        {/* Noindex Toggle */}
                        <div className="space-y-2 pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <label htmlFor="noindex" className="text-sm font-medium">
                                        Exclude from Search Engines
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                        When enabled, generated pages will include a meta robots tag (noindex, nofollow) to prevent search engines from indexing them.
                                    </p>
                                </div>
                                <Switch
                                    id="noindex"
                                    checked={formData.noindex}
                                    onCheckedChange={(checked) =>
                                        setFormData(prev => ({ ...prev, noindex: checked }))
                                    }
                                    aria-label="Exclude from search engines"
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* HTML Selectors Card */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>HTML Selectors</CardTitle>
                        <CardDescription>
                            Define selectors to target specific elements in the HTML template
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <Tabs defaultValue={selectorMode} onValueChange={setSelectorMode} className="w-full">
                            <TabsList className="grid w-full grid-cols-2 mb-4">
                                <TabsTrigger value="manual">Manual Selection</TabsTrigger>
                                <TabsTrigger value="dynamic">Dynamic Selection</TabsTrigger>
                            </TabsList>

                            <TabsContent value="manual" className="space-y-4">
                                {isManualSelectionDisabled && (
                                    <>
                                        <div className="mt-2 mb-4">
                                            <p className="text-sm text-amber-600">
                                                <strong>Note:</strong> Specifying a dynamic selector prefix will disable the manual selector option.
                                                Clear the prefix in the "Dynamic Selection" tab to re-enable manual selectors.
                                            </p>
                                        </div>
                                        <Alert variant="warning">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertTitle>Manual Selection Disabled</AlertTitle>
                                            <AlertDescription>
                                                Manual selection is disabled because a dynamic selector prefix is configured.
                                                To use manual selection, clear the dynamic selector prefix in the "Dynamic Selection" tab.
                                            </AlertDescription>
                                        </Alert>
                                    </>
                                )}

                                {formData.html_selectors.length > 0 && (
                                    <div className="border rounded-md overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Name
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Selector Path
                                                    </th>
                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {formData.html_selectors.map((selector, index) => (
                                                    <tr key={selector.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="text"
                                                                value={selector.name}
                                                                onChange={(e) => handleEditSelector(index, 'name', e.target.value)}
                                                                className={`w-full p-1 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-100 opacity-60' : ''}`}
                                                                disabled={isManualSelectionDisabled}
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <input
                                                                type="text"
                                                                value={selector.selector_path}
                                                                onChange={(e) => handleEditSelector(index, 'selector_path', e.target.value)}
                                                                className={`w-full p-1 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-100 opacity-60' : ''}`}
                                                                placeholder="e.g., #header > h1"
                                                                disabled={isManualSelectionDisabled}
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                onClick={() => handleRemoveSelector(selector.id)}
                                                                disabled={isManualSelectionDisabled}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className={`border rounded-md p-4 ${isManualSelectionDisabled ? 'bg-gray-100' : 'bg-gray-50'}`}>
                                    <h3 className="text-lg font-medium mb-3">Add New Selector</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div className="space-y-2">
                                            <label htmlFor="selectorName" className="text-sm font-medium">
                                                Name
                                            </label>
                                            <input
                                                id="selectorName"
                                                name="name"
                                                type="text"
                                                value={newSelector.name}
                                                onChange={handleSelectorInputChange}
                                                className={`w-full p-2 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-200 opacity-60' : ''}`}
                                                placeholder="e.g., Headline Text"
                                                disabled={isManualSelectionDisabled}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="selectorPath" className="text-sm font-medium">
                                                Selector Path
                                            </label>
                                            <input
                                                id="selectorPath"
                                                name="selector_path"
                                                type="text"
                                                value={newSelector.selector_path}
                                                onChange={handleSelectorInputChange}
                                                className={`w-full p-2 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-200 opacity-60' : ''}`}
                                                placeholder="e.g., #main-content > .headline"
                                                disabled={isManualSelectionDisabled}
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={handleAddSelector}
                                        disabled={!newSelector.name || !newSelector.selector_path || isManualSelectionDisabled}
                                        className={isManualSelectionDisabled ? 'opacity-60' : ''}
                                    >
                                        Add Selector
                                    </Button>
                                </div>

                                {formData.html_selectors.length === 0 && !isManualSelectionDisabled && (
                                    <div className="text-center py-4 text-gray-500">
                                        No selectors added yet. Add your first selector above.
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="dynamic" className="space-y-4">
                                <div className="border rounded-md p-4">
                                    <div className="space-y-4">
                                        <div>
                                            <h3 className="text-lg font-medium mb-3">Dynamic ID Selector Prefix</h3>
                                            <p className="text-gray-500 mb-4">
                                                Enter a prefix that will be used to automatically select all HTML elements with IDs starting with this prefix.
                                                For example, entering "dyn_" will select elements with IDs like "dyn_headline", "dyn_description", etc.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="dynamicPrefix" className="text-sm font-medium">
                                                ID Prefix
                                            </label>
                                            <input
                                                id="dynamicPrefix"
                                                name="dynamic_selector_prefix"
                                                type="text"
                                                value={formData.dynamic_selector_prefix}
                                                onChange={handleInputChange}
                                                className="w-full p-2 border rounded-md"
                                                placeholder="e.g., dyn_"
                                            />
                                        </div>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger className="flex items-center underline cursor-help">
                                                            <AlertCircle className="h-4 w-4 mr-1" />
                                                            How does this work?
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-xs p-3">
                                                            <p>
                                                                Dynamic selection automatically finds all HTML elements with IDs starting with your specified prefix.
                                                                The element ID after the prefix will be used as the selector name.
                                                                <br /><br />
                                                                Example: With prefix "dyn_", an element with ID "dyn_headline" will be selected with the name "headline".
                                                            </p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {generalError && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{generalError}</AlertDescription>
                    </Alert>
                )}

                <div className="flex justify-end gap-4">
                    <Button
                        variant="outline"
                        type="button"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isCreating}>
                        {isCreating ? "Creating..." : "Create Template"}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default TemplateNew;